import { type ReactNode, type Ref, useMemo, useRef } from "react";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { useSyncedAnimations } from "./useSyncedAnimations";
import styles from "./StatusPill.module.scss";

interface StatusPillProps {
    icon: ReactNode;
    label: string;
    /** Ambient "in progress" treatment: a spot orbiting the border plus label shimmer. */
    isAnimated?: boolean;
    className?: string;
    ref?: Ref<HTMLSpanElement>;
}

type StatusPillFrameProps = Omit<StatusPillProps, "isAnimated"> & {
    motion?: "running" | "paused";
};

const StatusPillFrame = ({
    icon,
    label,
    motion,
    className,
    ref,
}: Readonly<StatusPillFrameProps>) => (
    <span
        ref={ref}
        className={classNames(styles.pill, className)}
        data-motion={motion}
    >
        <span className={styles.icon}>{icon}</span>
        <span className={styles.label}>{label}</span>
    </span>
);

const AnimatedStatusPill = ({
    ref,
    ...props
}: Readonly<StatusPillFrameProps>) => {
    const elementRef = useRef<HTMLSpanElement>(null);
    const isPaused = useSyncedAnimations(elementRef);
    const mergedRef = useMemo(() => mergeRefs([ref, elementRef]), [ref]);
    return (
        <StatusPillFrame
            {...props}
            ref={mergedRef}
            motion={isPaused ? "paused" : "running"}
        />
    );
};

/**
 * Small bordered status label (icon + text), as on board cards. With
 * `isAnimated`, a light band sweeps the label and a slightly lighter spot
 * travels around the border; every animated pill on the page shares one phase.
 * Animated icons (`KickWorkingIcon`, `TaskInProgressSpinnerIcon`) loop under
 * the pill's `[data-motion="running"]`, so they share that phase and pause
 * with it off-screen or in a hidden tab.
 */
export const StatusPill = ({
    isAnimated = false,
    ...props
}: Readonly<StatusPillProps>) =>
    isAnimated ? (
        <AnimatedStatusPill {...props} />
    ) : (
        <StatusPillFrame {...props} />
    );
